import React, { useEffect, useMemo, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { complaintService } from '../../services/api';
import { useApiQuery } from '../../hooks/useApi';
import { userFacingError } from '../../i18n/userFacingError';
import FormSection from '../Common/FormSection';
import ComplaintPhotoSlider from './ComplaintPhotoSlider';

function unwrapFotos(raw) {
  return Array.isArray(raw) ? raw : raw?.data ?? [];
}

export default function ComplaintPhotoSection({
  complaintId,
  pendingFiles,
  onPendingFilesChange,
}) {
  const [localItems, setLocalItems] = useState([]);

  useEffect(() => {
    const next = (pendingFiles || []).map((file, idx) => ({
      id: `pending-${idx}-${file.name}`,
      src: URL.createObjectURL(file),
      file,
      alt: file.name,
    }));
    setLocalItems(next);
    return () => {
      next.forEach((item) => URL.revokeObjectURL(item.src));
    };
  }, [pendingFiles]);

  const { data: fotosRaw, refetch, isLoading } = useApiQuery(
    ['complaint-fotos', complaintId],
    async () => {
      const r = await complaintService.listComplaintFotos(complaintId);
      return unwrapFotos(r.data);
    },
    { enabled: !!complaintId }
  );
  const fotos = useMemo(() => unwrapFotos(fotosRaw), [fotosRaw]);

  const uploadMutation = useMutation(
    (file) => complaintService.uploadComplaintFoto(complaintId, file),
    {
      onSuccess: () => {
        toast.success('Foto enviada');
        refetch();
      },
      onError: (e) => toast.error(userFacingError(e, 'uploadError')),
    }
  );

  const deleteMutation = useMutation((fotoId) => complaintService.deleteComplaintFoto(fotoId), {
    onSuccess: () => {
      toast.success('Foto removida');
      refetch();
    },
    onError: (e) => toast.error(userFacingError(e, 'deleteError')),
  });

  const savedItems = fotos.map((ph) => ({
    id: ph.id,
    path: ph.url_path || `/api/v1/complaint/complaint-fotos/${ph.id}/file`,
    alt: ph.caption || 'Evidência fotográfica',
  }));

  return (
    <FormSection
      id="complaint-evidencias"
      title="Evidências fotográficas"
      description="JPG, PNG ou WEBP (máx. 15 MB). O slide segue o padrão do app."
      icon={ImageIcon}
      variant="slate"
    >
      {complaintId ? (
        isLoading ? (
          <p className="text-sm text-[#869990]">Carregando fotos…</p>
        ) : (
          <ComplaintPhotoSlider
            items={savedItems}
            adding={uploadMutation.isLoading}
            onAdd={(files) => files.forEach((f) => uploadMutation.mutate(f))}
            onRemove={(item) => {
              if (window.confirm('Remover esta foto?')) deleteMutation.mutate(item.id);
            }}
          />
        )
      ) : (
        <ComplaintPhotoSlider
          items={localItems}
          onAdd={(files) => onPendingFilesChange?.([...(pendingFiles || []), ...files])}
          onRemove={(item) => {
            onPendingFilesChange?.((pendingFiles || []).filter((f) => f !== item.file));
          }}
        />
      )}
    </FormSection>
  );
}
